import { type FC, useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { MultiSelect, type Option } from 'react-multi-select-component';
import { useNotifications } from '../../../modules/hooks';
import { useLocks } from '../../Dashboards/LockManagement/useLocks';
import { useAccessCodes } from '../../Dashboards/TemporaryAccessCodes/useAccessCodes';

interface EditLocksModalProps {
  show: boolean;
  uuid?: string;
  handleClose: () => void;
  onSaved?: () => void;
}

/**
 * Add/remove the locks on any enabled access code. Loads the property's locks for the
 * picker and pre-selects the code's current locks, then sends only the diff to the
 * `/locks` PATCH route (add + remove), leaving the rest of the code untouched.
 */
export const EditLocksModal: FC<EditLocksModalProps> = ({
  show,
  uuid,
  handleClose,
  onSaved,
}) => {
  const { setSimpleToasts } = useNotifications()!;
  const accessCodes = useAccessCodes();
  const locksService = useLocks();

  const [lockOptions, setLockOptions] = useState<Option[]>([]);
  const [currentLockUuids, setCurrentLockUuids] = useState<string[]>([]);
  const [selectedLocks, setSelectedLocks] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show || !uuid) return;
    setSelectedLocks([]);
    setCurrentLockUuids([]);
    setLoading(true);
    Promise.all([locksService.list(), accessCodes.get(uuid)])
      .then(([locks, record]) => {
        setLockOptions(locks.map((lock) => ({ label: lock.name, value: lock.uuid })));
        const current = record.locks ?? [];
        setCurrentLockUuids(current.map((lock) => lock.uuid));
        setSelectedLocks(current.map((lock) => ({ label: lock.name, value: lock.uuid })));
      })
      .catch(() =>
        setSimpleToasts({ type: 'danger', message: 'Failed to load locks', show: true }),
      )
      .finally(() => setLoading(false));
  }, [show, uuid, locksService, accessCodes, setSimpleToasts]);

  const { toAdd, toRemove } = useMemo(() => {
    const selectedUuids = selectedLocks.map((option) => option.value);
    return {
      toAdd: selectedUuids.filter((id) => !currentLockUuids.includes(id)),
      toRemove: currentLockUuids.filter((id) => !selectedUuids.includes(id)),
    };
  }, [selectedLocks, currentLockUuids]);

  const hasChanges = toAdd.length > 0 || toRemove.length > 0;

  const handleSave = async () => {
    if (!uuid || !hasChanges) return;
    setSaving(true);
    try {
      await accessCodes.updateLocks(uuid, {
        lockUuidsToAdd: toAdd,
        lockUuidsToRemove: toRemove,
      });
      setSimpleToasts({ type: 'success', message: 'Locks updated', show: true });
      onSaved?.();
      handleClose();
    } catch {
      setSimpleToasts({
        type: 'danger',
        message: 'Failed to update locks',
        show: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit locks</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <Form.Group>
            <Form.Label>Locks</Form.Label>
            <MultiSelect
              options={lockOptions}
              value={selectedLocks}
              onChange={setSelectedLocks}
              labelledBy="Select locks"
            />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button className="cancel-button" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          className="save-button"
          onClick={handleSave}
          disabled={saving || loading || selectedLocks.length === 0 || !hasChanges}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
